// IndexedDB persistence for user-uploaded video files (TRAVEL.mp4 + REVIEWS.mp4
// and any future uploads). Files stay 100% client-side — they never leave the
// browser. This lets the user refresh the page and keep their footage.
//
// Storage shape: a single object store "files" keyed by slot name. Each row:
//   { slot: "travel" | "reviews" | "pinterest_photo", blob: Blob, name: string,
//     size: number, type: string, savedAt: ISOString }

const DB_NAME = "buddypass_uploads_v1";
const STORE = "files";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "slot" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    let result;
    Promise.resolve(fn(store))
      .then((r) => { result = r; })
      .catch(reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
  });
}

export async function saveFile(slot, file) {
  const blob = file instanceof Blob ? file : new Blob([file]);
  await tx("readwrite", (s) => s.put({
    slot,
    blob,
    name: file?.name || `${slot}.bin`,
    size: file?.size || blob.size,
    type: file?.type || blob.type || "application/octet-stream",
    savedAt: new Date().toISOString(),
  }));
  return true;
}

export async function getFile(slot) {
  return tx("readonly", (s) => new Promise((res) => {
    const r = s.get(slot);
    r.onsuccess = () => res(r.result || null);
    r.onerror = () => res(null);
  }));
}

export async function removeFile(slot) {
  await tx("readwrite", (s) => s.delete(slot));
}

export async function listFiles() {
  return tx("readonly", (s) => new Promise((res) => {
    const r = s.getAll();
    r.onsuccess = () => res(r.result || []);
    r.onerror = () => res([]);
  }));
}

// Convenience: returns a usable File-like object with an object URL.
export async function getFileWithUrl(slot) {
  const row = await getFile(slot);
  if (!row) return null;
  return { ...row, url: URL.createObjectURL(row.blob) };
}
